import type { Language } from '../hooks/useTranslation';

/**
 * Supported languages in the chat application
 */
const SUPPORTED_LANGUAGES: Language[] = ['en', 'da'];

/**
 * Default fallback language when browser language is not supported
 */
const DEFAULT_LANGUAGE: Language = 'en';

/**
 * Detects the browser's preferred language and maps it to a supported language.
 *
 * - If browser language is English ('en', 'en-US', 'en-GB', etc.) → returns 'en'
 * - If browser language is Danish ('da', 'da-DK', etc.) → returns 'da'
 * - Any other language → returns 'en' (default)
 *
 * @returns The detected language code ('en' or 'da')
 *
 * @example
 * ```typescript
 * // Browser language is 'da-DK'
 * const lang = detectBrowserLanguage(); // Returns 'da'
 *
 * // Browser language is 'fr-FR'
 * const lang = detectBrowserLanguage(); // Returns 'en' (default)
 * ```
 */
export function detectBrowserLanguage(): Language {
  // Check if we're in a browser environment
  if (typeof navigator === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  // Get the browser's preferred languages (ordered by preference)
  const browserLanguages = navigator.languages || [navigator.language];

  for (const lang of browserLanguages) {
    // Extract the primary language code (e.g., 'en' from 'en-US')
    const primaryLang = lang.split('-')[0].toLowerCase();

    // Check if it's a supported language
    if (SUPPORTED_LANGUAGES.includes(primaryLang as Language)) {
      return primaryLang as Language;
    }
  }

  return DEFAULT_LANGUAGE;
}

/**
 * Gets the default language, preferring browser language detection.
 * Use this when no explicit language prop is provided.
 *
 * @returns The default language based on browser settings
 */
export function getDefaultLanguage(): Language {
  return detectBrowserLanguage();
}

